import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import {
  CURRENCIES, formatNumber, PERMISSIONS, SETTING_LABELS, SETTINGS_GROUPS, settingsUpdateSchema,
  type SettingKey, type Settings,
} from '@najah/core'
import {
  Alert, Button, Card, CardBody, CardHeader, cn, Input, Select, Spinner, Switch, useToast,
} from '@najah/ui'
import { settingsApi } from '../lib/settingsApi'
import { useAuth } from '../lib/auth'
import { useFormErrors } from '../lib/useFormErrors'

/** حقول منطقية وحقول اختيار — الباقي نصّي أو رقمي */
const BOOLEAN_FIELDS = new Set<SettingKey>([
  'tax.enabled', 'inventory.allowNegative', 'invoice.showTax', 'pos.printAfterSale',
])
const NUMBER_FIELDS = new Set<SettingKey>(['tax.defaultRate', 'inventory.lowStockThreshold'])

/** حقول تأخذ عرض البطاقة كاملاً: المفاتيح والنصوص الطويلة */
const FULL_WIDTH: (key: SettingKey) => boolean = (key) =>
  BOOLEAN_FIELDS.has(key) || key === 'store.address' || key === 'invoice.footer'

export function SettingsPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can(PERMISSIONS.SETTINGS_MANAGE)
  const { errors, formError, validate, handleApiError } = useFormErrors()

  const { data, isPending } = useQuery({ queryKey: ['settings'], queryFn: settingsApi.read })
  const [draft, setDraft] = useState<Partial<Settings>>({})

  // المسوّدة تُصفَّر عند وصول بيانات جديدة — لا نُبقي تعديلات فوق قيم قديمة
  useEffect(() => { setDraft({}) }, [data])

  const value = <K extends SettingKey>(key: K): Settings[K] =>
    (draft[key] !== undefined ? draft[key] : data?.[key]) as Settings[K]

  const set = <K extends SettingKey>(key: K, v: Settings[K]) =>
    setDraft((d) => ({ ...d, [key]: v }))

  const dirty = Object.keys(draft).length > 0

  const mutation = useMutation({
    mutationFn: () => {
      const parsed = validate(settingsUpdateSchema, draft)
      if (!parsed) throw new Error('VALIDATION')
      return settingsApi.update(parsed)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] })
      toast.success('حُفظت الإعدادات')
      setDraft({})
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر حفظ الإعدادات')
    },
  })

  if (isPending) {
    return <div className="grid h-64 place-items-center"><Spinner className="size-7 text-primary" /></div>
  }

  function renderField(key: SettingKey) {
    const meta = SETTING_LABELS[key]

    if (BOOLEAN_FIELDS.has(key)) {
      return (
        <Switch
          key={key}
          checked={value(key) as boolean}
          onChange={(e) => set(key, e.target.checked as never)}
          disabled={!canManage}
          label={meta.label}
          description={meta.hint}
        />
      )
    }

    if (key === 'currency.code') {
      return (
        <Select
          key={key}
          label={meta.label}
          value={value(key) as string}
          onChange={(e) => set(key, e.target.value as never)}
          disabled={!canManage}
          options={Object.values(CURRENCIES).map((c) => ({ value: c.code, label: `${c.nameAr} (${c.symbolAr})` }))}
          error={errors[key]}
        />
      )
    }

    if (key === 'locale.digits') {
      return (
        <Select
          key={key}
          label={meta.label}
          value={value(key) as string}
          onChange={(e) => set(key, e.target.value as never)}
          disabled={!canManage}
          options={[
            { value: 'latn', label: 'غربية — 0 1 2 3' },
            { value: 'arab', label: 'هندية — ٠ ١ ٢ ٣' },
          ]}
          hint={meta.hint}
          error={errors[key]}
        />
      )
    }

    return (
      <Input
        key={key}
        label={meta.label}
        hint={meta.hint}
        type={NUMBER_FIELDS.has(key) ? 'number' : 'text'}
        ltr={NUMBER_FIELDS.has(key) || key === 'store.phone' || key === 'store.taxId' || key === 'store.rc'}
        value={String(value(key) ?? '')}
        onChange={(e) => set(key, (NUMBER_FIELDS.has(key) ? Number(e.target.value) : e.target.value) as never)}
        disabled={!canManage}
        error={errors[key]}
      />
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 pb-24">
      <div>
        <h1 className="font-display text-2xl font-bold text-fg">الإعدادات</h1>
        <p className="mt-0.5 text-sm text-fg-muted">بيانات المكتبة والعملة وسلوك النظام</p>
      </div>

      {!canManage && (
        <Alert tone="info">
          يمكنك الاطّلاع على الإعدادات، لكن تعديلها يتطلّب صلاحية «إدارة الإعدادات».
        </Alert>
      )}

      {formError && <Alert tone="danger">{formError}</Alert>}

      {SETTINGS_GROUPS.map((group) => (
        <Card key={group.key}>
          <CardHeader title={group.title} description={group.description} />
          <CardBody className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {group.fields.map((f) => (
                <div key={f} className={cn(FULL_WIDTH(f as SettingKey) && 'sm:col-span-2')}>
                  {renderField(f as SettingKey)}
                </div>
              ))}
            </div>

            {group.key === 'money' && value('tax.enabled') && (
              <Alert tone="info">
                تفعيل الضريبة يُظهر حقولها في بطاقة المنتج والفواتير والتقارير.
                غيّر النسبة الافتراضية لتُقترَح تلقائياً على المنتجات الجديدة.
              </Alert>
            )}

            {group.key === 'inventory' && value('inventory.allowNegative') && (
              <Alert tone="warning">
                البيع تحت الصفر مُفعَّل. البيع دون اتصال يُقبل دائماً بحكم الواقع،
                لكن هذا يسمح به في البيع المتّصل أيضاً — استعمله فقط إن كان مخزونك
                غير دقيق مؤقّتاً، وراجع «فحص سلامة المخزون» بعدها.
              </Alert>
            )}
          </CardBody>
        </Card>
      ))}

      {/* شريط حفظ ثابت — التغييرات لا تُحفظ تلقائياً */}
      {canManage && dirty && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur-md lg:ps-64">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
            <p className="text-sm text-fg-muted">
              {formatNumber(Object.keys(draft).length)} إعداداً معدَّلاً لم يُحفظ
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setDraft({})} disabled={mutation.isPending}>
                تراجع
              </Button>
              <Button
                onClick={() => mutation.mutate()}
                loading={mutation.isPending}
                startIcon={<Save className="size-4" />}
              >
                حفظ
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
